import { useState, useMemo, useRef, useEffect, useCallback } from "react";
import { getDefrevData } from "../api";
import { useModel } from "../ModelProvider";
import { moduleLineLabel } from "../utils/leafLabels";
import { arrBreakdown, arrRowValue, hasAnyArr } from "../utils/arrBreakdown";
import { aeArrSeed } from "../utils/existingBook";   // W-154 (a): the chart series' "estimated" mark, the same rule as the table row
import { AreaChart, Area, BarChart, Bar, ComposedChart, Line, XAxis, YAxis, CartesianGrid, Tooltip, ResponsiveContainer, ReferenceLine, ReferenceArea, Cell } from "recharts";
import { groupByYear, filterForecast, getYear, formatMonthLabel } from "../engines/dateUtils";
import { quarterColumns } from "../utils/quarterColumns";
import { yearLabel } from "../utils/yearColumns";   // a partial year says so: "2026 (Oct-Dec)" (2026-11-02)
import { isOneMonthStart } from "../utils/companyWindow";   // S3: a Pre-Launch company's tiles read the forecast (2026-11-02)
import { computeRunway, runwayView, raiseInflows, runwayTile } from "../utils/cashRunway";   // the runway walk + wording the Scenarios tile uses; runwayTile = the ONE tile display (2026-11-03)
import { flatZeroAxis } from "../utils/chartAxis";   // the all-zero y-axis: one $0 tick, not five (2026-11-03)
import { netCashFlow } from "../utils/netCashFlow";
import { signColor } from "../utils/signColor";
// ★ item 7 (slug unification): the shared department-name slug — the same function
// runFullModel keys payrollHC with. This file previously declared an identical copy
// inline inside the deptStructure useMemo.
import { deptSlug } from "../utils/seatDepartments";
import OnboardingIncomplete from "../components/OnboardingIncomplete";
import ModelLoadError from "../components/ModelLoadError";

const r2 = (n) => Math.round(n * 100) / 100;

const fmt = (n) => {
  if (!n || n === 0) return "--";
  const a = Math.abs(n);
  const s = a >= 1e6 ? "$" + (a / 1e6).toFixed(2) + "M" : a >= 1e3 ? "$" + (a / 1e3).toFixed(1) + "K" : "$" + a.toFixed(2);
  return n < 0 ? "(" + s + ")" : s;
};

const fmtPct = (n) => {
  if (!n && n !== 0) return "--";

  return (n * 100).toFixed(1) + "%";
};

const fmtInt = (n) => {
  if (!n && n !== 0) return "--";
  return Math.round(n).toLocaleString();
};

/* ★ NULL-GUARDED AT THE FUNCTION, not at the call site — and that distinction is the
 * whole bug. 138ff5c ("Defensive null-guard on KPIDashboardPage cutoff.split() calls")
 * wrapped the `.split` it could SEE, inline in the scorecards below, and missed the one
 * a call deeper inside ML on the SAME LINE. `cutoff` is null for one frame after every
 * ModelProvider remount (App keys it on company id; cutoff is useState(null)), so
 * ML(null) threw "Cannot read properties of null (reading 'split')" during render and
 * the whole page unmounted to blank.
 * Guarding the VALUE here fixes every present and future call site at once, and matches
 * the form PayrollPage:26 and ScenariosPage:29 already use. */

const FONT = "'DM Sans',-apple-system,sans-serif";
const BLUE = "#2A6DB0";
const BLACK = "#1A1C2E";
const SL = { position: "sticky", left: 0, zIndex: 5, background: "inherit" };

const C_BLUE = "#2A6DB0";
const C_GREEN = "#059669";
const C_RED = "#DC2626";
const C_AMBER = "#D97706";
const C_PURPLE = "#7C3AED";
const C_SLATE = "#475569";
const C_TEAL = "#0D9488";

function getColumns(viewMode, allMonths) {
  if (!allMonths || !allMonths.length) return [];
  if (viewMode === "monthly") {
    return allMonths.map((m) => ({
      key: m, label: formatMonthLabel(m, "shortYear"), months: [m], year: parseInt(m.split("-")[0])
    }));
  }
  if (viewMode === "quarterly") {
    // This page's adapter was the correct one and predated the three broken
    // copies by six days. It is now the shared helper, called with the label
    // mode that reproduces this page's own idiom ("Q3 '26") byte for byte.
    return quarterColumns(allMonths, { label: "shortYear" });
  }
  const byY = groupByYear(allMonths);
  return Object.keys(byY).map(Number).sort((a, b) => a - b).map((y) => ({
    key: String(y), label: yearLabel(y, byY[y]), months: byY[y], year: y
  }));
}

function aggSum(data, ms) { return r2(ms.reduce((s, m) => s + (data[m] || 0), 0)); }
function aggAvg(data, ms) { const vals = ms.map((m) => data[m] || 0); return r2(vals.reduce((s, v) => s + v, 0) / vals.length); }
function aggLast(data, ms) { return data[ms[ms.length - 1]] || 0; }

export default function KPIDashboardPage({ companyId = 1 }) {
  const { isData, bsData, cfsData, cutoff, ensureFresh, rawData, contractorHC, payrollData, onboardingGap, modelState, modelError, frLines } = useModel();
  const allMonths = useMemo(() => rawData?.allMonths || [], [rawData]);
  const lastMonth = allMonths.length ? allMonths[allMonths.length - 1] : "";
  const companyName = rawData?.companyName || '';
  // ★ B3a (A4): honest-null. A tenant with no sweep target shows no sweep balance
  // rather than reading Semitek's account key (which it would not have anyway -> $0).
  const sweepAccountKey = rawData?.sweepAccountKey || null;
  const [loading, setLoading] = useState(true);
  const [dataReady, setDataReady] = useState(false);
  const [TOTAL_ARR_DB, setTotalArr] = useState({});
  const [HC_DB, setHC_DB] = useState({});
  // ★ HC UNIFICATION: liveHC is no longer computed here from the RAW API roster
  // (getEmployeesWithActuals -> monthlyData[m] > 0). It is a projection of the
  // engine's ONE headcount derivation (payrollData.headcount, engines/headcount.js),
  // in the ACTIVE view — this card answers "how many PEOPLE work here", so a
  // terminated employee's trailing commission-only month is NOT a head (that is the
  // PAYROLL-RUN view, which per-seat billing and the Payroll page use).
  //
  // Reading the engine also removes the second READ of the same fact: the roster
  // fetch this page used to make for counting is gone, and the count can no longer
  // go stale against the model the rest of the page renders.
  //
  // Key shape is unchanged (department slugs + "unmapped" + fte + total_hc), so the
  // HC overlay below and the legacy Semitek path are untouched.
  // KPI 6b: derive the FTE headcount structure from the tenant's department_mappings
  // (department_name → per-dept bucket; is_category → subtotal grouping). Empty
  // department_mappings (cid=1/cid=2 legacy) → { isDynamic:false } → the code falls back
  // to the hardcoded Semitek deptMap + fixed rows below (byte-identical legacy behavior).
  const HC_CAT_ORDER = ["COGS", "S&M", "R&D", "G&A"];
  const HC_CAT_CONTRACTOR = { "COGS": "contractors_cogs", "S&M": "contractors_sm", "R&D": "contractors_rd", "G&A": "contractors_ga" };
  const HC_CAT_CONTRACTOR_LABEL = { "COGS": "COGS Contractors", "S&M": "S&M Contractors", "R&D": "R&D Contractors", "G&A": "G&A Contractors" };
  const deptStructure = useMemo(() => {
    const rows = rawData?.deptMappings || [];
    /* ★ P4-a — the `useLegacyDeptFallback === true` arm is DELETED. It returned
     * `{ isDynamic: false }`, the Semitek breakdown, and it required a company flag that is
     * false on all four live tenants with NO writer anywhere in the product (the only UPDATE
     * that ever set it targeted cid=1/cid=2, deleted 2026-08-12). It never executed.
     * A tenant with no mappings takes the dynamic path with zero department keys, so the
     * table shows the 'unmapped' row + totals and NAMES the gap — which is what already
     * happened for every tenant. */
    if (!rows.length) {
      return { isDynamic: true, deptMap: {}, depts: [], fteKeys: [], byCategory: {}, catOrder: [], deptsUnmapped: true };
    }
    // ★ item 7: the local `slug` copy (character-identical to the shared deptSlug,
    // including the `|| "dept"` fallback) is DELETED — imported instead.
    const deptMap = {}; const depts = []; const seen = new Set();
    rows.forEach((r) => {
      const key = deptSlug(r.department_name);
      deptMap[r.department_name] = key;
      if (!seen.has(key)) { seen.add(key); depts.push({ key, label: r.department_name, category: r.is_category }); }
    });
    const fteKeys = depts.map((d) => d.key);
    const byCategory = {};
    depts.forEach((d) => { const c = d.category || "Other"; (byCategory[c] = byCategory[c] || []).push(d); });
    const catOrder = [...HC_CAT_ORDER.filter((c) => byCategory[c]), ...Object.keys(byCategory).filter((c) => !HC_CAT_ORDER.includes(c))];
    return { isDynamic: true, deptMap, depts, fteKeys, byCategory, catOrder };
  // eslint-disable-next-line react-hooks/exhaustive-deps
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [rawData?.deptMappings]);

  const liveHC = useMemo(() => {
    const hcAct = payrollData?.headcount?.active;
    if (!hcAct) return null;
    const keys = [...(payrollData.headcount.keys || []), "unmapped"];
    const hc = {};
    keys.forEach(k => { hc[k] = {}; });
    hc.fte = {}; hc.total_hc = {};
    (allMonths || []).forEach(m => {
      keys.forEach(k => { hc[k][m] = hcAct.byKey[k]?.[m] || 0; });
      const total = hcAct.total[m] || 0;
      hc.fte[m] = total;
      hc.total_hc[m] = total;
    });
    return hc;
  }, [payrollData, allMonths]);
  // Merge: actuals from DB, forecast from combined kpiARR (all revenue modules)
  const TOTAL_ARR = useMemo(() => {
    const merged = { ...TOTAL_ARR_DB };
    const kpiARR = rawData?.kpiARR;
    const cut = cutoff || lastMonth;
    // KPI 6a Cp2: the historical (m<=cut) side reads the per-engine Historical ARR
    // (actuals) grids (aeq + cq existing_arr) WHEN PRESENT — this fills the historical
    // chart bars AND the card's close-month value (lastARR = TOTAL_ARR[cutoff]) without
    // any card-render change — falling back to the kpi_total_arr DB snapshot when absent.
    // cid=3 with empty grids → no overlay → merged = TOTAL_ARR_DB (unchanged).
    const aeArr = rawData?.aeqSettings?.existing_arr || {};
    const cqArr = rawData?.cqSettings?.existing_arr || {};
    new Set([...Object.keys(aeArr), ...Object.keys(cqArr)]).forEach(m => {
      if (m > cut) return; // historical side only; forecast stays engine-driven (kpiARR)
      const sum = r2((parseFloat(aeArr[m]) || 0) + (parseFloat(cqArr[m]) || 0));
      if (sum > 0) merged[m] = sum;
    });
    if (kpiARR) {
      Object.entries(kpiARR).forEach(([m, v]) => {
        if (m > cut && v) merged[m] = v?.total || v;
      });
    }
    return merged;
  }, [TOTAL_ARR_DB, isData, cutoff, lastMonth, rawData?.aeqSettings, rawData?.cqSettings]);

  // Live HC: actuals from DB, forecast FTE from live employee data, contractors from DB
  const HC = useMemo(() => {
    const cut = cutoff || lastMonth;
    const base = HC_DB || {};
    // KPI 6b: dynamic (department_mappings-driven) path — per-dept FTE buckets + per-
    // is_category subtotals. Contractor buckets (contractors_*) stay tenant-generic.
    if (deptStructure.isDynamic) {
      const { fteKeys, byCategory, catOrder } = deptStructure;
      const contractorBuckets = ["contractors_cogs","contractors_sm","contractors_ga","contractors_rd"];
      const subKeys = catOrder.map((c) => "sub_" + c);
      const allKeys = [...fteKeys, "unmapped", ...contractorBuckets, "contractors_total", "fte", "total_hc", ...subKeys];
      const merged = {};
      allKeys.forEach((k) => { merged[k] = { ...(base[k] || {}) }; });
      if (liveHC) {
        [...fteKeys, "unmapped", "fte", "total_hc"].forEach((k) => {
          Object.entries(liveHC[k] || {}).forEach(([m, v]) => { if (m > cut) merged[k][m] = v; });
        });
      }
      if (contractorHC) {
        [...contractorBuckets, "contractors_total"].forEach((k) => {
          Object.entries(contractorHC[k] || {}).forEach(([m, v]) => { if (m > cut) merged[k][m] = v; });
        });
      }
      filterForecast(allMonths, cut).forEach((m) => {
        merged.contractors_total[m] = contractorBuckets.reduce((s, k) => s + (merged[k]?.[m] || 0), 0);
        merged.total_hc[m] = (merged.fte?.[m] || 0) + merged.contractors_total[m];
        catOrder.forEach((cat) => {
          const fteSum = (byCategory[cat] || []).reduce((s, d) => s + (merged[d.key]?.[m] || 0), 0);
          const contr = merged[HC_CAT_CONTRACTOR[cat]]?.[m] || 0;
          merged["sub_" + cat][m] = fteSum + contr;
        });
      });
      return merged;
    }
    // ---- legacy Semitek path (empty department_mappings — unchanged) ----
    const keys = ["cs","content","growth","contractors_cogs","contractors_sm","total_sm","ga","contractors_ga","product","engineering","contractors_rd","total_rd","total_hc","fte","contractors_total"];
    const merged = {};
    keys.forEach(k => { merged[k] = { ...(base[k] || {}) }; });

    if (liveHC) {
      // Overlay all months with live FTE data
      ["cs","content","growth","ga","product","engineering","fte"].forEach(k => {
        Object.entries(liveHC[k] || {}).forEach(([m, v]) => {
          if (m > cut) merged[k][m] = v;
        });
      });
    }

    // Overlay contractor HC from live vendor engine for forecast months
    if (contractorHC) {
      ["contractors_cogs","contractors_sm","contractors_ga","contractors_rd","contractors_total"].forEach(k => {
        Object.entries(contractorHC[k] || {}).forEach(([m, v]) => {
          if (m > cut) merged[k][m] = v;
        });
      });
    }

    // Recompute totals for forecast months (every forecast month in this tenant's window)
    filterForecast(allMonths, cut).forEach(m => {
      merged.total_sm[m] = (merged.content?.[m]||0) + (merged.growth?.[m]||0) + (merged.contractors_sm?.[m]||0);
      merged.total_rd[m] = (merged.product?.[m]||0) + (merged.engineering?.[m]||0) + (merged.contractors_rd?.[m]||0);
      merged.contractors_total[m] = (merged.contractors_cogs?.[m]||0) + (merged.contractors_sm?.[m]||0) + (merged.contractors_ga?.[m]||0) + (merged.contractors_rd?.[m]||0);
      merged.total_hc[m] = (merged.fte?.[m]||0) + (merged.contractors_total?.[m]||0);
    });

    return merged;
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [HC_DB, liveHC, contractorHC, cutoff, allMonths, lastMonth, deptStructure]);
  const GAAP_REV = isData.total_revenue || {};
  const GROSS_PROFIT = isData.gross_profit || {};
  const GP_MARGIN = useMemo(() => {
    const m = {}; Object.keys(GAAP_REV).forEach(k => { m[k] = GAAP_REV[k] ? r2(GROSS_PROFIT[k]/GAAP_REV[k]) : 0; }); return m;
  }, [isData]);
  const EBITDA_DATA = isData.ebitda || {};
  const NET_INCOME = isData.net_income || {};
  const TOTAL_OPEX = isData.total_operating_expenses || isData.total_sga || {};
  const CASH_ON_HAND = useMemo(() => {
    const c = {}; const tc = bsData.total_clearing||{}; const mm = bsData[sweepAccountKey]||{};
    Object.keys(tc).forEach(k => { c[k] = r2((tc[k]||0)+(mm[k]||0)); }); return c;
  }, [bsData, sweepAccountKey]);
  // Net Burn = CFS Operating + Investing cash flow (signed; negative = cash consumed).
  // Financing (fundraises + the internal money-market/FRB sweep, `mm_sweep` → total_fin) is
  // EXCLUDED — the correct definition of operating cash burn. This replaces the prior MoM-Δ-of-
  // CASH_ON_HAND basis, which folded historical fundraises into "burn" (e.g. the +$40M 2021-06
  // Series-A raise). Reads existing cfsData — display-only, no compute touch.
  const NET_BURN = useMemo(() => {
    const b = {}; const oper = cfsData.total_oper || {};
    Object.keys(oper).forEach(m => { b[m] = netCashFlow(cfsData, m); });
    return b;
  }, [cfsData]);
  const loadData = useCallback(async () => {
    try { await ensureFresh();
      const res = await getDefrevData(companyId);
      const d = res.data;
      if (d.kpi_total_arr) setTotalArr(d.kpi_total_arr);
      if (d.kpi_hc) setHC_DB(d.kpi_hc);
      setDataReady(true);
    } catch (err) { console.error("Failed to load KPI data:", err); }
    finally { setLoading(false); }
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [companyId, ensureFresh]);
  useEffect(() => { loadData(); }, [loadData]);
  const [viewMode, setViewMode] = useState("monthly");
  const [activeSection, setActiveSection] = useState("overview");

  const columns = useMemo(() => getColumns(viewMode, allMonths), [viewMode, allMonths]);
  const cC = (col) => { const last = col.months[col.months.length - 1]; return last <= cutoff ? BLUE : BLACK; };
  const isYE = (col) => {
    const last = col.months[col.months.length - 1];
    return last.endsWith("-12") || col.key.endsWith("Q4");
  };

  const kpiARR = rawData?.kpiARR || {};
  const kpiARRConfig = rawData?.kpiARRConfig || {};

  /* W-085: the chart reads the ONE ARR-by-module builder (utils/arrBreakdown.js), like the table and Scenarios. */
  const chartData = useMemo(() => { if (!dataReady) return []; const arrB = arrBreakdown({ kpiARR, kpiARRConfig, aeqSettings: rawData?.aeqSettings, cqSettings: rawData?.cqSettings, cutoff, months: allMonths }); return allMonths.map((m) => {
    const isForecast = m > cutoff;
    const arrObj = kpiARR[m];
    const base = {
      m, label: formatMonthLabel(m, "shortYear"), forecast: isForecast ? 1 : 0,
      arr: TOTAL_ARR[m] || 0,
      cash: CASH_ON_HAND[m] || 0,
      burn: NET_BURN[m] || 0,
      rev: GAAP_REV[m] || 0,
      gp: GROSS_PROFIT[m] || 0,
      ebitda: EBITDA_DATA[m] || 0,
      ni: NET_INCOME[m] || 0,
      hc: HC.total_hc?.[m] || 0,
      fte: HC.fte?.[m] || 0,
      contractors: HC.contractors_total?.[m] || 0,
    };
    if (isForecast && arrObj) {
      base.arr_ae = arrRowValue(arrB, 'ae_quota', m);
      base.arr_cq = arrRowValue(arrB, 'company_quota', m);
      (kpiARRConfig.miscLines || []).forEach(ml => {
        base[`arr_misc_${ml.id}`] = arrRowValue(arrB, 'misc_' + ml.id, m);
      });
    } else {
      // KPI 6c: historical months stack the per-engine existing_arr grids (arrBreakdown.hist) under the same
      // arr_ae / arr_cq bars as the forecast side; a tenant with no grids keeps the single arr_historical bar.
      const { ae: aeH, cq: cqH } = arrB.hist[m];
      if (aeH > 0 || cqH > 0) {
        base.arr_ae = aeH;
        base.arr_cq = cqH;
      } else {
        base.arr_historical = TOTAL_ARR[m] || 0;
      }
    }
    return base;
  });
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [cutoff, dataReady, kpiARR, kpiARRConfig, allMonths, rawData?.aeqSettings, rawData?.cqSettings]);

  const lastCash = CASH_ON_HAND[cutoff] || 0;
  const burnArr = allMonths.filter((m) => m <= cutoff).slice(-3).map((m) => NET_BURN[m] || 0);
  const avgBurn = burnArr.length ? r2(burnArr.reduce((s, v) => s + v, 0) / burnArr.length) : 0;
  const runwayMonths = avgBurn < 0 ? Math.floor(lastCash / Math.abs(avgBurn)) : 99;
  // U4 (2026-10-30): the months avgBurn averages (the same filter as burnArr), for the runway caption -- computed, never typed.
  const burnMonths = allMonths.filter((m) => m <= cutoff).slice(-3);
  const burnSpan = !burnMonths.length ? "" : burnMonths.length === 1 ? formatMonthLabel(burnMonths[0], "shortFull")
    : (getYear(burnMonths[0]) === getYear(burnMonths[burnMonths.length - 1]) ? formatMonthLabel(burnMonths[0], "short") : formatMonthLabel(burnMonths[0], "shortFull"))
      + "\u2013" + formatMonthLabel(burnMonths[burnMonths.length - 1], "shortFull");
  const runwaySub = burnMonths.length ? "based on the last " + (burnMonths.length === 1 ? "" : burnMonths.length + " ") + "closed month" + (burnMonths.length === 1 ? "'s" : "s'") + " burn (" + burnSpan + ")" : "at current burn";
  /* S3 (2026-11-02, the Pre-Launch batch): a PRE-LAUNCH company (utils/companyWindow.js isOneMonthStart: historical start ==
     close month) has ONE closed month -- its opening balance sheet -- so the closed-month burn above is 0 and the runway reads
     "N/A". Its two tiles read the FORECAST instead: the runway is the Scenarios tile's own walk and wording (utils/cashRunway.js,
     the same cash and net-cash-flow series), the net cash flow the average of the first 3 forecast months. Every other company
     keeps the two tiles exactly as above. */
  const preLaunch = isOneMonthStart(rawData?.historicalStart, cutoff);
  const plForecast = preLaunch ? allMonths.filter((m) => m > cutoff) : [];
  const plFirst3 = plForecast.slice(0, 3);
  const plAvgBurn = plFirst3.length ? r2(plFirst3.reduce((s, m) => s + (NET_BURN[m] || 0), 0) / plFirst3.length) : 0;
  const plRunway = preLaunch ? runwayView(computeRunway(CASH_ON_HAND, NET_BURN, cutoff, allMonths), CASH_ON_HAND, plForecast, fmt) : null;
  /* (2026-11-02, Brian option (c)) and, when the forecast holds a funding round, the SAME walk WITH the planned raise: the rounds'
     net proceeds in their close months (utils/cashRunway.js raiseInflows -- the engine's own fin_fr_* lines). The figure above stays
     "on current cash" (operating + investing only). With no round in the forecast the tile is exactly the one before. */
  const plRaise = preLaunch ? raiseInflows(cfsData, frLines, plForecast) : {};
  const plHasRaise = Object.keys(plRaise).length > 0;
  /* 2026-11-03 (Brian: one runway treatment and one display): with a planned raise the tile is utils/cashRunway.js runwayTile -- the
     SAME owner the Scenarios tile calls: the headline is the runway WITH the raise, then the estimate past the forecast, the lowest
     cash and, muted, the runway on current cash only. Without a raise nothing here changes (plRunway above). The NON-Pre-Launch tile
     below is a different basis (the last 3 closed months' burn) and is deliberately left as it is (diffs/runway-arr-tiles-build.txt
     Part 0d -- Brian's call). */
  const plRunwayRaise = plHasRaise ? runwayTile(CASH_ON_HAND, NET_BURN, cutoff, allMonths, plForecast, plRaise, fmt) : null;
  const lastARR = TOTAL_ARR[cutoff] || 0;
  /* 2026-11-03 (Brian): a company with NO ARR -- this page's own ARR series zero or empty in every month (utils/arrBreakdown.js
     hasAnyArr) -- shows MONTHLY REVENUE (the IS Total Revenue line) in the Total ARR tile and chart. A company with ARR is unchanged. */
  const noArr = !hasAnyArr(TOTAL_ARR, allMonths);

  // KPI 6d: actuals-region shade band (x1/x2 are LABEL strings — XAxis dataKey="label",
  // a category axis; month keys would be invisible/misplaced). Null when no actuals month.
  const actualsBand = (allMonths.length && allMonths[0] <= cutoff)
    ? { x1: formatMonthLabel(allMonths[0], "shortYear"), x2: formatMonthLabel(cutoff, "shortYear") } : null;
  // First forecast month — used by the Headcount-card fallback below.
  const seamMonth = allMonths.find((m) => m > cutoff);
  // Headcount card fallback: when the close-month HC is empty (cid=3 has no kpi_hc
  // snapshot → total_hc[cutoff] is 0), read the first forecast month + tag "(forecast)".
  const hcCloseEmpty = !(HC.total_hc?.[cutoff]);
  const hcSrcMonth = (hcCloseEmpty && seamMonth) ? seamMonth : cutoff;
  const hcCardVal = HC.total_hc?.[hcSrcMonth] || 0;
  const hcCardSub = fmtInt(HC.fte?.[hcSrcMonth] || 0) + " FTE + " + fmtInt(HC.contractors_total?.[hcSrcMonth] || 0) + " contractors"
    + ((hcCloseEmpty && seamMonth) ? " · " + formatMonthLabel(hcSrcMonth, "short") + " '" + (hcSrcMonth || "").split("-")[0].slice(2) + " (forecast)" : "");

  const getVal = (data, col, agg) => {
    if (agg === "sum") return aggSum(data, col.months);
    if (agg === "avg") return aggAvg(data, col.months);
    return aggLast(data, col.months);
  };

  const renderRow = (label, data, aggType, fmtFn, opts) => {
    if (!opts) opts = {};
    const indent = (opts.level || 0) * 16;
    const isH = opts.type === "header";
    const bg = isH ? "#F5F6FA" : opts.bold ? "#FAFBFE" : "#fff";
    const fw = isH || opts.bold ? 600 : 400;
    const fs = isH ? 10 : 11;
    const bb = opts.bold ? "2px solid #E2E5F0" : "1px solid #F5F6FA";
    return (
      <tr key={label + (opts.keySuffix || "")}>
        <td style={{ ...SL, padding: "6px 12px 6px " + (12 + indent) + "px", fontSize: fs, fontWeight: fw, color: isH ? "#6B7280" : BLACK, background: bg, borderBottom: bb, whiteSpace: "nowrap", minWidth: 260 }}>{label}</td>
        {columns.map((col) => (
          <td key={col.key} style={{ padding: "6px 8px", textAlign: "right", fontSize: 11, fontWeight: fw, color: opts.signColor && !isH ? signColor(getVal(data, col, aggType), cC(col)) : cC(col), background: bg, borderBottom: bb, fontFamily: "'Space Mono',monospace", whiteSpace: "nowrap", minWidth: 90, borderRight: isYE(col) ? "2px solid rgba(61,133,198,0.15)" : "none" }}>
            {isH ? "" : fmtFn(getVal(data, col, aggType))}
          </td>
        ))}
      </tr>
    );
  };

  const spacerRow = (id) => (
    <tr key={id}><td colSpan={columns.length + 1} style={{ height: 10, background: "#FAFBFE" }}></td></tr>
  );

  /* ★ The ARR chart's two module series and their table rows are named for the operator by
     the IS line each module forecasts. ⛔ `dataKey` (arr_ae / arr_cq) is the series
     IDENTITY and is untouched; `name` reaches only this file's CustomTooltip — there is no
     <Legend> — and the one place a series name is COMPARED ("GP Margin", below) is a
     different series on a different chart. */
  const aeLineLabel = moduleLineLabel(rawData?.glMappings, rawData?.revMappings, 'ae_quota', 'Account Executive Quota');
  const cqLineLabel = moduleLineLabel(rawData?.glMappings, rawData?.revMappings, 'company_quota', 'Company Quota');

  const CustomTooltip = ({ active, payload, label }) => {
    if (!active || !payload || !payload.length) return null;
    const isARR = payload.some(p => p.dataKey && String(p.dataKey).startsWith("arr_"));
    const arrTotal = isARR ? r2(payload.filter(p => p.dataKey && String(p.dataKey).startsWith("arr_")).reduce((s, p) => s + (p.value || 0), 0)) : 0;
    return (
      <div style={{ background: "#fff", border: "1px solid #E2E5F0", borderRadius: 8, padding: "10px 14px", fontSize: 11, fontFamily: FONT, boxShadow: "0 4px 12px rgba(0,0,0,0.08)" }}>
        <div style={{ fontWeight: 700, marginBottom: 6, color: BLACK }}>{label}</div>
        {payload.map((p, i) => (
          <div key={i} style={{ display: "flex", alignItems: "center", gap: 6, marginBottom: 2 }}>
            <div style={{ width: 8, height: 8, borderRadius: "50%", background: p.color }} />
            <span style={{ color: "#6B7280" }}>{p.name}:</span>
            <span style={{ fontWeight: 600, fontFamily: "'Space Mono',monospace" }}>
              {p.name === "GP Margin" && typeof p.value === "number"
                ? (p.value * 100).toFixed(1) + "%"
                : (typeof p.value === "number" && Math.abs(p.value) > 100 ? fmt(p.value) : p.value)}
            </span>
          </div>
        ))}
        {isARR && arrTotal > 0 && <>
          <div style={{ borderTop: "1px solid #E2E5F0", marginTop: 4, paddingTop: 4, display: "flex", alignItems: "center", gap: 6 }}>
            <span style={{ fontWeight: 700, color: BLACK }}>Total ARR:</span>
            <span style={{ fontWeight: 700, fontFamily: "'Space Mono',monospace", color: BLACK }}>{fmt(arrTotal)}</span>
          </div>
        </>}
      </div>
    );
  };

  // ★ Item-5 READER — whole-model empty state before the loading/spinner guard.
  /* ★ CRASHROOT PART 2 — these two returns read `modelState`, the provider's single
     terminal-exit answer, instead of inferring the model's state from onboardingGap
     alone. 'empty' REUSES the existing OnboardingIncomplete JSX unchanged (the gap
     object is still what names it); 'error' is the state that previously had NO
     reader at all — a thrown fetch cleared `loading` with allMonths===[] and this
     page spun forever (reproduced pre-fix in diffs/_crashroot-verifyA-pre.txt).
     ⛔ The `!allMonths.length` guard below STAYS, and stays BELOW these returns:
        belt AND braces, and above them it would send a named-empty tenant back to a
        spinner (diffs/crashroot-phase0.txt §4). */
  if (modelState === 'error') return <ModelLoadError message={modelError} />;
  if (modelState === 'empty') return <OnboardingIncomplete gap={onboardingGap} />;
  /* ★ `!allMonths.length` IS LOAD-BEARING, NOT BELT-AND-BRACES. This page's `loading` is
   * its OWN flag: loadData's `finally` clears it as soon as `ensureFresh()` resolves — and
   * ensureFresh RETURNS IMMEDIATELY WITHOUT COMPUTING while the cutoff seed is still
   * pending (ModelProvider gate 1). A resolved promise there means "I did not need to
   * compute" and "I am not ready yet" alike, so `loading === false` never implied
   * `rawData !== null`. On that frame allMonths is [] and the subtitle below called
   * getYear(allMonths[0]) -> undefined.split -> the whole page blanked.
   * This is CLAUDE.md §9.5 checklist item 1, and the exact guard 7 of the 10
   * allMonths-using pages already carry (BalanceSheet:349, CashFlow:118,
   * DeferredRevenue:538, IncomeStatement:304, Payroll:1191, RevenueBuild:761,
   * Scenarios:1101). KPI was the only one with a loading guard that omitted it. */
  if (modelState === 'loading' || loading || !allMonths.length) return <div style={{display:"flex",justifyContent:"center",alignItems:"center",height:"100%",fontFamily:FONT}}>Loading KPI data...</div>;

  /* ★ BUILT AFTER THE GUARDS, DELIBERATELY. This array literal used to sit ~9 lines
   * ABOVE them, in the declaration phase — and a loading guard short-circuits the JSX
   * RETURN phase, never the declaration phase (CLAUDE.md §9.5 checklist item 5; the
   * fmtBE crash 6e65e0d is the same defect). So on the one frame after a tenant switch,
   * where cutoff is still null and `loading` is still false, it was built anyway and
   * threw. Below the returns, that frame short-circuits to the loading/empty state and
   * these cards are never constructed.
   * ⓘ ALSO WHAT THIS UN-BLOCKS: the onboardingGap return above is the named empty state
   *   for a tenant with no close_month — it was unreachable, because the throw happened
   *   first. It is now the state such a tenant actually lands in.
   * Contents, order and colors are UNCHANGED — this was a move, not a rewrite. */
  const scorecards = [
    noArr
      /* the month the neighbouring tiles use (the close); a Pre-Launch company's first forecast month, marked "(forecast)" as the
         Headcount tile marks it -- its close month is its opening balance sheet, with no revenue. */
      ? ((preLaunch && seamMonth)
        ? { label: "Monthly Revenue", value: fmt(GAAP_REV[seamMonth] || 0), sub: formatMonthLabel(seamMonth, "short") + " '" + seamMonth.split("-")[0].slice(2) + " (forecast)", color: C_BLUE }
        : { label: "Monthly Revenue", value: fmt(GAAP_REV[cutoff] || 0), sub: formatMonthLabel(cutoff, "short") + " " + (cutoff || "").split("-")[0], color: C_BLUE })
      : { label: "Total ARR", value: fmt(lastARR), sub: formatMonthLabel(cutoff, "short") + " " + (cutoff || "").split("-")[0], color: C_BLUE },
    { label: "Cash on Hand", value: fmt(lastCash), sub: formatMonthLabel(cutoff, "short") + " " + (cutoff || "").split("-")[0], color: C_GREEN },
    preLaunch
      ? { label: "Monthly Net Cash Flow", value: fmt(plAvgBurn), valueColor: signColor(plAvgBurn, BLACK), sub: "Pre-Launch: first 3 forecast months", color: C_RED }
      : { label: "Monthly Net Cash Flow", value: fmt(avgBurn), valueColor: signColor(avgBurn, BLACK), sub: "3-mo trailing avg, closed months", color: C_RED },
    preLaunch
      ? (plHasRaise
        ? { label: "Cash Runway", value: plRunwayRaise.v, lines: plRunwayRaise.lines, sub: "Pre-Launch: from the forecast, with planned raises", color: C_AMBER }
        : { label: "Cash Runway", value: plRunway.v, sub: "Pre-Launch: from the forecast" + (plRunway.sub ? "; " + plRunway.sub : ""), color: C_AMBER })
      : { label: "Cash Runway", value: runwayMonths < 99 ? runwayMonths + " months" : "N/A", sub: runwaySub, color: C_AMBER },
    { label: "Headcount", value: fmtInt(hcCardVal), sub: hcCardSub, color: C_PURPLE },
  ];

  return (
    <div style={{ fontFamily: FONT, background: "#F0F1F5", height: "100%", display: "flex", overflow: "hidden" }}>
      <link href="https://fonts.googleapis.com/css2?family=DM+Sans:wght@400;500;700&family=Space+Mono:wght@400;700&display=swap" rel="stylesheet" />

      <div style={{ flex: 1, display: "flex", flexDirection: "column", overflow: "hidden" }}>
        <div style={{ padding: "16px 24px", display: "flex", justifyContent: "space-between", alignItems: "center", flexWrap: "wrap", gap: 12, flexShrink: 0 }}>
          <div>
            <h1 style={{ margin: 0, fontSize: 22, fontWeight: 700, color: BLACK, letterSpacing: "-0.5px" }}>KPI Dashboard</h1>
            {/* ★ THE LENGTH CHECK COMES FIRST. This used to open with
                `const a=getYear(allMonths[0])` and only then test `a==null` — a guard on the
                RESULT of the call that throws, which is no guard at all. The gate above now
                stops an empty allMonths reaching here, but the line is made correct ON ITS
                OWN so it does not depend on a guard 30 lines away. Same output for a real
                allMonths. ⓘ The identical unguarded IIFE is copy-pasted on four other pages
                (BalanceSheet:355, CashFlow:123, DeferredRevenue:546, IncomeStatement:314);
                they were latent only because those pages carry the gate. ALL FOUR ARE
                NOW FIXED (plus RevenueBuild:793, which guards actualsMonths rather than
                allMonths) — this note used to read "filed, not fixed here", and that
                became false the moment they landed. The 13 duplicate ML/MLS copies remain:
                consolidation onto dateUtils.formatMonthLabel is deferred, not done. */}
            <p style={{ margin: "2px 0 0", fontSize: 12, color: "#8B8FA3" }}>{companyName||"—"} - {(()=>{if(!allMonths.length)return"—";const a=getYear(allMonths[0]);const b=getYear(allMonths[allMonths.length-1]);return a==null?"—":a===b?String(a):`${a}–${b}`;})()}</p>
          </div>
          <div style={{ display: "flex", gap: 8, alignItems: "center", flexWrap: "wrap" }}>
            <div style={{ display: "flex", borderRadius: 6, border: "1px solid #D1D5DB", overflow: "hidden" }}>
              {[["monthly","Monthly"],["quarterly","Quarterly"],["annual","Annual"]].map(([v,l]) => (
                <button key={v} onClick={() => setViewMode(v)} style={{ padding: "6px 14px", fontSize: 11, fontWeight: 600, cursor: "pointer", border: "none", background: viewMode === v ? "#E8EAFF" : "#fff", color: viewMode === v ? "#3D5AF1" : "#4B5563" }}>{l}</button>
              ))}
            </div>
            <div style={{ width: 1, height: 24, background: "#E2E5F0" }} />
            <div style={{ display: "flex", alignItems: "center", gap: 6, padding: "5px 12px", borderRadius: 6, border: "1px solid #D1D5DB", background: "#fff" }}>
              <span style={{ fontSize: 10, fontWeight: 600, color: "#6B7280", whiteSpace: "nowrap" }}>Last Closed Month</span>
              {/* READOUT, not a control. See BalanceSheetPage for the reasoning.
                  This page built its label as formatMonthLabel(m,"short") + a hand
                  appended year; "shortFull" produces the identical string for all
                  36 window months, so this also retires that second spelling. */}
              <span style={{ fontSize: 11, fontWeight: 700, color: BLACK }}>{formatMonthLabel(cutoff, "shortFull")}</span>
            </div>
          </div>
        </div>

        <div style={{ flex: 1, overflow: "auto", padding: "0 24px 24px", minHeight: 0 }}>
          <div style={{ display: "grid", gridTemplateColumns: "repeat(5, 1fr)", gap: 12, marginBottom: 20 }}>
            {scorecards.map((card, i) => (
              <div key={i} style={{ background: "#fff", borderRadius: 10, padding: "16px 18px", border: "1px solid #E2E5F0", borderTop: "3px solid " + card.color }}>
                <div style={{ fontSize: 10, fontWeight: 600, color: "#8B8FA3", textTransform: "uppercase", letterSpacing: "0.5px", marginBottom: 8 }}>{card.label}</div>
                <div style={{ fontSize: 22, fontWeight: 700, color: card.valueColor || BLACK, fontFamily: "'Space Mono',monospace", letterSpacing: "-1px" }}>{card.value}</div>
                {(card.lines || []).map((ln, j) => <div key={j} style={{ fontSize: ln.muted ? 10 : 11, fontWeight: ln.muted ? 400 : 600, color: ln.muted ? "#8B8FA3" : BLACK, marginTop: 4, overflowWrap: "anywhere" }}>{ln.text}</div>)}
                <div style={{ fontSize: 10, color: "#8B8FA3", marginTop: 4 }}>{card.sub}</div>
              </div>
            ))}
          </div>

          <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: 16, marginBottom: 20 }}>
            {noArr ? (
            <div style={{ background: "#fff", borderRadius: 10, padding: "18px 20px", border: "1px solid #E2E5F0" }}>
              <div style={{ fontSize: 12, fontWeight: 700, color: BLACK, marginBottom: 12 }}>Monthly Revenue</div>
              <ResponsiveContainer width="100%" height={200}>
                <BarChart data={chartData}>
                  <CartesianGrid strokeDasharray="3 3" stroke="#F0F1F5" />
                  <XAxis dataKey="label" tick={{ fontSize: 9, fill: "#8B8FA3" }} interval={2} />
                  <YAxis tick={{ fontSize: 9, fill: "#8B8FA3" }} tickFormatter={(v) => "$" + (v / 1e3).toFixed(0) + "K"} {...flatZeroAxis(chartData, ["rev"])} />
                  <Tooltip content={<CustomTooltip />} />
                  <Bar dataKey="rev" name="Revenue" fill={C_BLUE} radius={[2, 2, 0, 0]}>
                    {chartData.map((d) => <Cell key={d.m} fillOpacity={d.m <= cutoff ? 0.6 : 1} />)}
                  </Bar>
                </BarChart>
              </ResponsiveContainer>
            </div>
            ) : (
            <div style={{ background: "#fff", borderRadius: 10, padding: "18px 20px", border: "1px solid #E2E5F0" }}>
              <div style={{ fontSize: 12, fontWeight: 700, color: BLACK, marginBottom: 12 }}>Total ARR</div>
              <ResponsiveContainer width="100%" height={200}>
                <BarChart data={chartData} stackOffset="none">
                  <CartesianGrid strokeDasharray="3 3" stroke="#F0F1F5" />
                  <XAxis dataKey="label" tick={{ fontSize: 9, fill: "#8B8FA3" }} interval={2} />
                  <YAxis tick={{ fontSize: 9, fill: "#8B8FA3" }} tickFormatter={(v) => "$" + (v / 1e6).toFixed(1) + "M"} {...flatZeroAxis(chartData, ["arr_historical", "arr_ae", "arr_cq", ...(kpiARRConfig.miscLines || []).map(ml => "arr_misc_" + ml.id)])} />
                  <Tooltip content={<CustomTooltip />} />
                  <Bar dataKey="arr_historical" name="Historical ARR" stackId="arr" fill={C_BLUE} opacity={0.6} radius={[0, 0, 0, 0]} />
                  {kpiARRConfig.hasAEQuota && <Bar dataKey="arr_ae" name={aeLineLabel + (aeArrSeed(rawData?.aeqSettings, cutoff).estimated ? " (estimated)" : "")} stackId="arr" fill="#27AE60" radius={[0, 0, 0, 0]}>
                    {chartData.map((d) => <Cell key={d.m} fillOpacity={d.m <= cutoff ? 0.6 : 1} />)}
                  </Bar>}
                  {kpiARRConfig.hasCQ && <Bar dataKey="arr_cq" name={cqLineLabel} stackId="arr" fill="#E67E22" radius={[0, 0, 0, 0]}>
                    {chartData.map((d) => <Cell key={d.m} fillOpacity={d.m <= cutoff ? 0.6 : 1} />)}
                  </Bar>}
                  {(kpiARRConfig.miscLines || []).map((ml, i) => {
                    const colors = ["#8E44AD", "#16A085", "#C0392B"];
                    return <Bar key={ml.id} dataKey={`arr_misc_${ml.id}`} name={ml.name + " ARR"} stackId="arr" fill={colors[i % colors.length]} radius={[0, 0, 0, 0]} />;
                  })}
                </BarChart>
              </ResponsiveContainer>
            </div>
            )}

            <div style={{ background: "#fff", borderRadius: 10, padding: "18px 20px", border: "1px solid #E2E5F0" }}>
              <div style={{ fontSize: 12, fontWeight: 700, color: BLACK, marginBottom: 12 }}>Cash on Hand</div>
              <ResponsiveContainer width="100%" height={200}>
                <AreaChart data={chartData}>
                  <defs><linearGradient id="cashG" x1="0" y1="0" x2="0" y2="1"><stop offset="5%" stopColor={C_GREEN} stopOpacity={0.15} /><stop offset="95%" stopColor={C_GREEN} stopOpacity={0} /></linearGradient></defs>
                  <CartesianGrid strokeDasharray="3 3" stroke="#F0F1F5" />
                  <XAxis dataKey="label" tick={{ fontSize: 9, fill: "#8B8FA3" }} interval={2} />
                  <YAxis tick={{ fontSize: 9, fill: "#8B8FA3" }} tickFormatter={(v) => "$" + (v / 1e6).toFixed(1) + "M"} {...flatZeroAxis(chartData, ["cash"])} />
                  <Tooltip content={<CustomTooltip />} />
                  {actualsBand && <ReferenceArea x1={actualsBand.x1} x2={actualsBand.x2} fill="#2A6DB0" fillOpacity={0.06} ifOverflow="extendDomain" />}
                  <Area type="monotone" dataKey="cash" name="Cash" stroke={C_GREEN} fill="url(#cashG)" strokeWidth={2} dot={false} />
                </AreaChart>
              </ResponsiveContainer>
            </div>
          </div>

          <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: 16, marginBottom: 20 }}>
            <div style={{ background: "#fff", borderRadius: 10, padding: "18px 20px", border: "1px solid #E2E5F0" }}>
              <div style={{ fontSize: 12, fontWeight: 700, color: BLACK, marginBottom: 12 }}>Revenue vs EBITDA</div>
              <ResponsiveContainer width="100%" height={200}>
                <ComposedChart data={chartData}>
                  <CartesianGrid strokeDasharray="3 3" stroke="#F0F1F5" />
                  <XAxis dataKey="label" tick={{ fontSize: 9, fill: "#8B8FA3" }} interval={2} />
                  <YAxis tick={{ fontSize: 9, fill: "#8B8FA3" }} tickFormatter={(v) => "$" + (v / 1e3).toFixed(0) + "K"} {...flatZeroAxis(chartData, ["rev", "ebitda"])} />
                  <Tooltip content={<CustomTooltip />} />
                  {actualsBand && <ReferenceArea x1={actualsBand.x1} x2={actualsBand.x2} fill="#2A6DB0" fillOpacity={0.06} ifOverflow="extendDomain" />}
                  <ReferenceLine y={0} stroke="#D1D5DB" strokeDasharray="3 3" />
                  <Bar dataKey="rev" name="Revenue" fill={C_BLUE} opacity={0.7} radius={[2, 2, 0, 0]} />
                  <Line type="monotone" dataKey="ebitda" name="EBITDA" stroke={C_RED} strokeWidth={2} dot={false} />
                </ComposedChart>
              </ResponsiveContainer>
            </div>

            <div style={{ background: "#fff", borderRadius: 10, padding: "18px 20px", border: "1px solid #E2E5F0" }}>
              <div style={{ fontSize: 12, fontWeight: 700, color: BLACK, marginBottom: 12 }}>Monthly Net Cash Flow</div>
              <ResponsiveContainer width="100%" height={200}>
                <BarChart data={chartData}>
                  <CartesianGrid strokeDasharray="3 3" stroke="#F0F1F5" />
                  <XAxis dataKey="label" tick={{ fontSize: 9, fill: "#8B8FA3" }} interval={2} />
                  <YAxis tick={{ fontSize: 9, fill: "#8B8FA3" }} tickFormatter={(v) => "$" + (v / 1e3).toFixed(0) + "K"} {...flatZeroAxis(chartData, ["burn"])} />
                  <Tooltip content={<CustomTooltip />} />
                  <ReferenceLine y={0} stroke="#D1D5DB" strokeDasharray="3 3" />
                  <Bar dataKey="burn" name="Net Cash Flow" fill={C_AMBER} radius={[2, 2, 0, 0]}>
                    {chartData.map((d) => <Cell key={d.m} fill={signColor(d.burn, C_AMBER)} fillOpacity={d.m <= cutoff ? 0.6 : 1} />)}
                  </Bar>
                </BarChart>
              </ResponsiveContainer>
            </div>
          </div>

          <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: 16, marginBottom: 20 }}>
            <div style={{ background: "#fff", borderRadius: 10, padding: "18px 20px", border: "1px solid #E2E5F0" }}>
              <div style={{ fontSize: 12, fontWeight: 700, color: BLACK, marginBottom: 12 }}>Headcount</div>
              <ResponsiveContainer width="100%" height={200}>
                <AreaChart data={chartData}>
                  <CartesianGrid strokeDasharray="3 3" stroke="#F0F1F5" />
                  <XAxis dataKey="label" tick={{ fontSize: 9, fill: "#8B8FA3" }} interval={2} />
                  <YAxis tick={{ fontSize: 9, fill: "#8B8FA3" }} />
                  <Tooltip content={<CustomTooltip />} />
                  {actualsBand && <ReferenceArea x1={actualsBand.x1} x2={actualsBand.x2} fill="#2A6DB0" fillOpacity={0.06} ifOverflow="extendDomain" />}
                  <Area type="stepAfter" dataKey="fte" name="FTE" stackId="1" stroke={C_PURPLE} fill={C_PURPLE} fillOpacity={0.3} strokeWidth={2} />
                  <Area type="stepAfter" dataKey="contractors" name="Contractors" stackId="1" stroke={C_SLATE} fill={C_SLATE} fillOpacity={0.2} strokeWidth={2} />
                </AreaChart>
              </ResponsiveContainer>
            </div>

            <div style={{ background: "#fff", borderRadius: 10, padding: "18px 20px", border: "1px solid #E2E5F0" }}>
              <div style={{ fontSize: 12, fontWeight: 700, color: BLACK, marginBottom: 12 }}>Gross Profit</div>
              <ResponsiveContainer width="100%" height={200}>
                <ComposedChart data={chartData}>
                  <CartesianGrid strokeDasharray="3 3" stroke="#F0F1F5" />
                  <XAxis dataKey="label" tick={{ fontSize: 9, fill: "#8B8FA3" }} interval={2} />
                  <YAxis yAxisId="left" tick={{ fontSize: 9, fill: "#8B8FA3" }} tickFormatter={(v) => "$" + (v / 1e3).toFixed(0) + "K"} {...flatZeroAxis(chartData, ["gp"])} />
                  <YAxis yAxisId="right" orientation="right" tick={{ fontSize: 9, fill: "#8B8FA3" }} tickFormatter={(v) => (v * 100).toFixed(0) + "%"} domain={[0, 1]} />
                  <Tooltip content={<CustomTooltip />} />
                  {actualsBand && <ReferenceArea yAxisId="left" x1={actualsBand.x1} x2={actualsBand.x2} fill="#2A6DB0" fillOpacity={0.06} ifOverflow="extendDomain" />}
                  <Bar yAxisId="left" dataKey="gp" name="Gross Profit" fill={C_TEAL} opacity={0.6} radius={[2, 2, 0, 0]} />
                  <Line yAxisId="right" type="monotone" dataKey={(d) => (GAAP_REV[d.m] ? GROSS_PROFIT[d.m] / GAAP_REV[d.m] : 0)} name="GP Margin" stroke={C_TEAL} strokeWidth={2} dot={false} />
                </ComposedChart>
              </ResponsiveContainer>
            </div>
          </div>

          <div style={{ background: "#fff", borderRadius: 12, border: "1px solid #E2E5F0", overflow: "hidden", marginBottom: 20 }}>
            <style>{".kpi-s::-webkit-scrollbar{width:8px;height:8px}.kpi-s::-webkit-scrollbar-thumb{background:#C5CAD9;border-radius:4px}"}</style>
            <div className="kpi-s" style={{ overflowX: "auto" }}>
              <table style={{ borderCollapse: "separate", borderSpacing: 0, width: "max-content", minWidth: "100%" }}>
                <thead style={{ position: "sticky", top: 0, zIndex: 10 }}>
                  <tr style={{ background: "#FAFBFE" }}>
                    <th style={{ ...SL, padding: "8px 12px", textAlign: "left", fontSize: 10, fontWeight: 700, color: "#A0A3B8", background: "#FAFBFE", zIndex: 12 }}></th>
                    {(() => {
                      const yearsByYear = groupByYear(allMonths);
                      const years = Object.keys(yearsByYear).map(Number).sort((a, b) => a - b);
                      const lastYear = years[years.length - 1];
                      return years.map((y) => {
                        const yMonths = yearsByYear[y];
                        const lM = yMonths[yMonths.length - 1];
                        const fM = yMonths[0];
                        const aA = lM <= cutoff;
                        const aF = fM > cutoff;
                        const lb = aA ? "ACTUALS" : aF ? "FORECAST" : "ACTUALS / FORECAST";
                        const cn = columns.filter((c) => c.year === y).length;
                        return <th key={y} colSpan={cn} style={{ padding: "6px", textAlign: "center", fontSize: 10, fontWeight: 700, color: aF ? "#3D85C6" : BLUE, background: "#FAFBFE", borderRight: y !== lastYear ? "2px solid rgba(61,133,198,0.15)" : "none" }}>{y} - {lb}</th>;
                      });
                    })()}
                  </tr>
                  <tr style={{ background: "#fff" }}>
                    <th style={{ ...SL, padding: "8px 12px", textAlign: "left", fontSize: 9, fontWeight: 600, color: "#8B8FA3", minWidth: 260, background: "#fff", zIndex: 12, borderBottom: "2px solid #E2E5F0" }}>
                      {viewMode === "monthly" ? "Monthly" : viewMode === "quarterly" ? "Quarterly" : "Annual"} View
                    </th>
                    {columns.map((c) => (
                      <th key={c.key} style={{ padding: "8px 4px", textAlign: "center", fontSize: 9, fontWeight: 600, color: cC(c), whiteSpace: "nowrap", background: "#fff", minWidth: 90, borderRight: isYE(c) ? "2px solid rgba(61,133,198,0.15)" : "none", borderBottom: "2px solid #E2E5F0" }}>{c.label}</th>
                    ))}
                  </tr>
                </thead>
                <tbody>
                  {(() => {
                    // ARR breakdown rows -- W-085: ONE builder (utils/arrBreakdown.js); the row set is the engine's kpiARRConfig.
                    const arrB = arrBreakdown({ kpiARR, kpiARRConfig, aeqSettings: rawData?.aeqSettings, cqSettings: rawData?.cqSettings, cutoff, months: allMonths });
                    return <>
                      {arrB.rows.map(r => r.kind === 'misc'
                        ? renderRow(r.name + " ARR", r.byMonth, "last", fmt, { level: 1, keySuffix: "_misc_" + r.miscId })
                        : renderRow((r.kind === 'ae_quota' ? aeLineLabel : cqLineLabel) + " ARR" + (r.estimated ? " (estimated)" : ""), r.byMonth, "last", fmt, { level: 1 }))}
                    </>;
                  })()}
                  {renderRow("Total ARR", TOTAL_ARR, "last", fmt, { bold: true })}
                  {spacerRow("s1")}
                  {renderRow("GAAP Revenue", GAAP_REV, "sum", fmt, {})}
                  {renderRow("Gross Profit", GROSS_PROFIT, "sum", fmt, {})}
                  {renderRow("GP Margin %", GP_MARGIN, "avg", fmtPct, {})}
                  {renderRow("Total OpEx", TOTAL_OPEX, "sum", fmt, {})}
                  {renderRow("EBITDA", EBITDA_DATA, "sum", fmt, { bold: true })}
                  {viewMode !== "monthly" && renderRow("  Monthly Avg", EBITDA_DATA, "avg", fmt, { level: 1, keySuffix: "_eavg" })}
                  {renderRow("Net Income", NET_INCOME, "sum", fmt, { bold: true })}
                  {viewMode !== "monthly" && renderRow("  Monthly Avg", NET_INCOME, "avg", fmt, { level: 1, keySuffix: "_niavg" })}
                  {spacerRow("s2")}
                  {renderRow("Cash on Hand", CASH_ON_HAND, "last", fmt, { bold: true })}
                  {renderRow("Net Cash Flow", NET_BURN, "sum", fmt, { signColor: true })}
                  {viewMode !== "monthly" && renderRow("  Monthly Avg", NET_BURN, "avg", fmt, { level: 1, keySuffix: "_nbavg", signColor: true })}
                  {spacerRow("s3")}
                  {/* ★ KPI 6b: one FTE row per department (label = department_name), grouped
                      under is_category subtotals; contractor buckets placed in-group.
                      ⓘ THE TERNARY IS GONE, NOT HALF-COLLAPSED. This used to be
                      `deptStructure.isDynamic ? (…) : <the Semitek breakdown>` — a false arm
                      hardcoding Customer Success / Content / Growth / Product / Engineering.
                      It could not render: deptStructure has exactly TWO returns (both in its
                      useMemo) and BOTH set `isDynamic: true` — the arm that returned false was
                      deleted by P4-a with the useLegacyDeptFallback flag it needed. The field
                      still has a live reader in the HC useMemo, so it is not orphaned. */}
                  {(() => {
                    const rows = [];
                    deptStructure.catOrder.forEach(cat => {
                      (deptStructure.byCategory[cat] || []).forEach(d => {
                        rows.push(renderRow(d.label, HC[d.key] || {}, "last", fmtInt, { level: 1, keySuffix: "_" + d.key }));
                      });
                      const cb = HC_CAT_CONTRACTOR[cat];
                      if (cb) rows.push(renderRow(HC_CAT_CONTRACTOR_LABEL[cat], HC[cb] || {}, "last", fmtInt, { level: 1, keySuffix: "_" + cb }));
                      rows.push(renderRow("Total " + cat, HC["sub_" + cat] || {}, "last", fmtInt, { bold: true, level: 1, keySuffix: "_sub_" + cat }));
                    });
                    if (allMonths.some(m => (HC.unmapped?.[m] || 0) > 0)) {
                      rows.push(renderRow("Unmapped", HC.unmapped || {}, "last", fmtInt, { level: 1, keySuffix: "_unmapped" }));
                    }
                    rows.push(renderRow("Total Headcount", HC.total_hc || {}, "last", fmtInt, { bold: true }));
                    rows.push(renderRow("FTE", HC.fte || {}, "last", fmtInt, { level: 1 }));
                    rows.push(renderRow("Contractors", HC.contractors_total || {}, "last", fmtInt, { level: 1 }));
                    return rows;
                  })()}
                </tbody>
              </table>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
}
